import { experimentTemplateSchema } from '@ecomm/shared-ninetailed'
import type { TypeOf } from '@simplisafe/ewok'
import { z } from 'zod'

const idSchema = z.object({
  id: z.string()
})

const guidedSystemBuilderSchemaDraft = z.object({
  __typename: z.literal('QuoteWizard').optional(),
  sys: idSchema,
  id: z
    .union([z.string(), idSchema])
    .transform(val => (typeof val === 'string' ? val : val.id)),
  jebbitUrl: z.string().optional(),
  type: z.enum([
    'embedded',
    'floating',
    'guarantee',
    'hero',
    'monitoring_component',
    'popup',
    'nav'
  ]),
  buttonText: z.string().nullish()
})

const guidedSystemBuilderParsedSchemaDraft =
  guidedSystemBuilderSchemaDraft.extend({
    id: z.string()
  })

const ntFeatureFlagSchema = z.object({
  __typename: z.literal('NtFeatureFlag'),
  sys: idSchema.optional(),
  id: z
    .union([z.string(), idSchema])
    .optional()
    .transform(val => {
      if (!val) {
        return `feature-flag-${Math.random()}`
      }
      return typeof val === 'string' ? val : val.id
    }),
  title: z.string().optional(),
  json: z
    .object({
      id: z.string(),
      tag: z.string()
    })
    .optional()
    .default({ id: '', tag: '' })
})

const discriminatedUnion = z.discriminatedUnion('__typename', [
  guidedSystemBuilderSchemaDraft,
  ntFeatureFlagSchema
])

const experimentSchema = experimentTemplateSchema
  .extend({
    variants: z.object({
      items: z.array(discriminatedUnion).default([])
    })
  })
  .transform(val => ({
    ...val,
    id: typeof val.id === 'string' ? val.id : val.id.id,
    audience: val.audience
      ? {
          ...val.audience,
          id:
            typeof val.audience.id === 'string'
              ? val.audience.id
              : val.audience.id.id
        }
      : null
  }))

const experimentSchemaParsed = experimentTemplateSchema
  .extend({
    variants: z.object({
      items: z.array(discriminatedUnion).default([])
    })
  })
  .transform(val => ({
    ...val,
    id: typeof val.id === 'string' ? val.id : val.id.id,
    audience: val.audience
      ? {
          ...val.audience,
          id:
            typeof val.audience.id === 'string'
              ? val.audience.id
              : val.audience.id.id
        }
      : null
  }))

export const guidedSystemBuilderSchema = guidedSystemBuilderSchemaDraft.extend({
  ntExperiencesCollection: z.object({
    items: z.array(experimentSchema).default([])
  })
})

export const guidedSystemBuilderParsedSchema =
  guidedSystemBuilderParsedSchemaDraft.extend({
    ntExperiencesCollection: z.object({
      items: z.array(experimentSchemaParsed).default([])
    })
  })

export type GuidedSystemBuilderSchema = TypeOf<typeof guidedSystemBuilderSchema>
